import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, CheckCircle, Clock, Zap, Settings, Play, Pause, Edit, Trash2, Plus, Bot, Mail, FileText, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useAIAgents } from '@/hooks/useAIAgents';

interface AutomationRule {
  id: string;
  name: string;
  description: string;
  trigger: string;
  conditions: any[];
  actions: any[];
  enabled: boolean;
  created_at: string;
  last_executed?: string;
  execution_count: number;
}

interface WorkflowExecution {
  id: string;
  rule_id: string;
  bill_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at?: string;
  actions_executed: any[];
  error_message?: string;
}

export const WorkflowAutomation = () => {
  const { toast } = useToast();
  const { agents, executeAgent, isExecuting } = useAIAgents();
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger: 'bill_processed',
    conditions: [],
    actions: [],
    enabled: true
  });

  // Sample automation rules
  useEffect(() => {
    const sampleRules: AutomationRule[] = [
      {
        id: '1',
        name: 'High Confidence Auto-Approval',
        description: 'Automatically approve bills with confidence > 95%',
        trigger: 'bill_processed',
        conditions: [
          { type: 'confidence', operator: '>', value: 95 },
          { type: 'anomalies', operator: 'empty' }
        ],
        actions: [
          { type: 'approve_bill' },
          { type: 'send_notification', target: 'manager' }
        ],
        enabled: true,
        created_at: '2024-01-15T10:00:00Z',
        last_executed: '2024-01-25T15:30:00Z',
        execution_count: 45
      },
      {
        id: '2',
        name: 'Low Confidence Review Alert',
        description: 'Send alerts for bills with confidence < 70%',
        trigger: 'bill_processed',
        conditions: [
          { type: 'confidence', operator: '<', value: 70 }
        ],
        actions: [
          { type: 'set_status', value: 'review' },
          { type: 'send_email', template: 'low_confidence_alert' },
          { type: 'create_task', assignee: 'quality_team' }
        ],
        enabled: true,
        created_at: '2024-01-10T09:00:00Z',
        last_executed: '2024-01-25T12:15:00Z',
        execution_count: 23
      },
      {
        id: '3',
        name: 'Large Amount Verification',
        description: 'Flag bills over $1000 for additional verification',
        trigger: 'bill_processed',
        conditions: [
          { type: 'amount', operator: '>', value: 1000 }
        ],
        actions: [
          { type: 'add_flag', value: 'high_amount' },
          { type: 'require_approval', level: 'manager' },
          { type: 'log_event', message: 'High amount bill requires verification' }
        ],
        enabled: true,
        created_at: '2024-01-08T14:00:00Z',
        last_executed: '2024-01-24T16:45:00Z',
        execution_count: 12
      },
      {
        id: '4',
        name: 'Daily Processing Summary',
        description: 'Send daily summary of processed bills',
        trigger: 'schedule',
        conditions: [
          { type: 'time', value: '18:00' },
          { type: 'weekday', operator: 'not', value: ['saturday', 'sunday'] }
        ],
        actions: [
          { type: 'generate_report', reportType: 'daily_summary' },
          { type: 'send_email', template: 'daily_summary', recipients: ['admin@company.com'] }
        ],
        enabled: true,
        created_at: '2024-01-05T08:00:00Z',
        last_executed: '2024-01-25T18:00:00Z',
        execution_count: 20
      },
      {
        id: '5',
        name: 'Anomaly Detection Response',
        description: 'Immediate response to detected anomalies',
        trigger: 'anomaly_detected',
        conditions: [
          { type: 'anomaly_severity', operator: '>=', value: 'high' }
        ],
        actions: [
          { type: 'pause_processing' },
          { type: 'send_alert', urgency: 'high' },
          { type: 'create_incident', priority: 'high' }
        ],
        enabled: false,
        created_at: '2024-01-12T11:00:00Z',
        last_executed: '2024-01-20T09:30:00Z',
        execution_count: 3
      }
    ];

    setRules(sampleRules);

    // Sample executions
    const sampleExecutions: WorkflowExecution[] = [
      {
        id: 'exec_1',
        rule_id: '1',
        bill_id: 'bill_123',
        status: 'completed',
        started_at: '2024-01-25T15:30:00Z',
        completed_at: '2024-01-25T15:30:15Z',
        actions_executed: [
          { type: 'approve_bill', result: 'success' },
          { type: 'send_notification', result: 'success' }
        ]
      },
      {
        id: 'exec_2',
        rule_id: '2',
        bill_id: 'bill_124',
        status: 'completed',
        started_at: '2024-01-25T12:15:00Z',
        completed_at: '2024-01-25T12:15:30Z',
        actions_executed: [
          { type: 'set_status', result: 'success' },
          { type: 'send_email', result: 'success' },
          { type: 'create_task', result: 'success' }
        ]
      },
      {
        id: 'exec_3',
        rule_id: '3',
        bill_id: 'bill_125',
        status: 'failed',
        started_at: '2024-01-25T10:00:00Z',
        completed_at: '2024-01-25T10:00:45Z',
        actions_executed: [
          { type: 'add_flag', result: 'success' },
          { type: 'require_approval', result: 'failed' }
        ],
        error_message: 'Failed to create approval request'
      }
    ];

    setExecutions(sampleExecutions);
  }, []);

  const toggleRule = async (ruleId: string, enabled: boolean) => {
    setRules(prev => prev.map(rule => 
      rule.id === ruleId ? { ...rule, enabled } : rule
    ));

    toast({
      title: enabled ? "Rule enabled" : "Rule disabled",
      description: `Automation rule has been ${enabled ? 'enabled' : 'disabled'}.`,
    });
  };

  const deleteRule = async (ruleId: string) => {
    setRules(prev => prev.filter(rule => rule.id !== ruleId));
    toast({
      title: "Rule deleted",
      description: "Automation rule has been deleted.",
    });
  };

  const createRule = async () => {
    if (!newRule.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for the automation rule.",
        variant: "destructive",
      });
      return;
    }

    const rule: AutomationRule = {
      id: Date.now().toString(),
      ...newRule,
      created_at: new Date().toISOString(),
      execution_count: 0
    };

    setRules(prev => [...prev, rule]);
    setNewRule({
      name: '',
      description: '',
      trigger: 'bill_processed',
      conditions: [],
      actions: [],
      enabled: true
    });
    setIsCreatingRule(false);

    toast({
      title: "Rule created",
      description: "Automation rule has been created successfully.",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default">Completed</Badge>;
      case 'running':
        return <Badge variant="secondary">Running</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  const getTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case 'bill_processed':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'schedule':
        return <Clock className="h-4 w-4 text-green-500" />;
      case 'anomaly_detected':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Zap className="h-4 w-4 text-gray-500" />;
    }
  };

  // Calculate automation metrics
  const enabledRules = rules.filter(rule => rule.enabled).length;
  const totalExecutions = rules.reduce((sum, rule) => sum + rule.execution_count, 0);
  const recentExecutions = executions.filter(exec => 
    new Date(exec.started_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
  ).length;
  const successRate = executions.length > 0 
    ? Math.round((executions.filter(exec => exec.status === 'completed').length / executions.length) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Workflow Automation</h2>
          <p className="text-muted-foreground">
            Configure automated workflows for bill processing and quality control
          </p>
        </div>
        <Button onClick={() => setIsCreatingRule(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Rule
        </Button>
      </div>

      {/* Automation Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Rules</CardTitle>
            <Bot className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{enabledRules}</div>
            <p className="text-xs text-muted-foreground">
              {rules.length} total rules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalExecutions}</div>
            <p className="text-xs text-muted-foreground">
              All time executions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Activity</CardTitle>
            <Zap className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{recentExecutions}</div>
            <p className="text-xs text-muted-foreground">
              Executions in 24h
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{successRate}%</div>
            <p className="text-xs text-muted-foreground">
              Execution success
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Automation Tabs */}
      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rules">Automation Rules</TabsTrigger>
          <TabsTrigger value="executions">Recent Executions</TabsTrigger>
          <TabsTrigger value="templates">Rule Templates</TabsTrigger>
          <TabsTrigger value="agents">AI Agent Integration</TabsTrigger>
          <TabsTrigger value="scheduling">Scheduled Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle>Automation Rules</CardTitle>
              <CardDescription>
                Manage automated workflows and business rules
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Executions</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{rule.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {rule.description}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getTriggerIcon(rule.trigger)}
                          <span className="text-sm">{rule.trigger.replace('_', ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={(checked) => toggleRule(rule.id, checked)}
                          />
                          <Badge variant={rule.enabled ? 'default' : 'outline'}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{rule.execution_count}</Badge>
                      </TableCell>
                      <TableCell>
                        {rule.last_executed ? (
                          <span className="text-sm">
                            {format(new Date(rule.last_executed), 'MMM dd, HH:mm')}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteRule(rule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="executions">
          <Card>
            <CardHeader>
              <CardTitle>Recent Executions</CardTitle>
              <CardDescription>
                Monitor automation rule executions and results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead>Bill ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map((execution) => {
                    const rule = rules.find(r => r.id === execution.rule_id);
                    const duration = execution.completed_at
                      ? Math.round((new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime()) / 1000)
                      : null;

                    return (
                      <TableRow key={execution.id}>
                        <TableCell>
                          <span className="font-medium">{rule?.name || 'Unknown Rule'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">
                            {execution.bill_id.substring(0, 8)}...
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(execution.status)}
                            {getStatusBadge(execution.status)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {format(new Date(execution.started_at), 'MMM dd, HH:mm:ss')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {duration ? (
                            <span className="text-sm font-mono">{duration}s</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {execution.actions_executed.length} actions
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span>Auto-Approval</span>
                </CardTitle>
                <CardDescription>
                  Automatically approve high-confidence bills
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>• Confidence greater than 95%</div>
                  <div>• No anomalies detected</div>
                  <div>• Amount less than $500</div>
                </div>
                <Button className="w-full mt-4" variant="outline">
                  Use Template
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-blue-500" />
                  <span>Alert Notifications</span>
                </CardTitle>
                <CardDescription>
                  Send alerts for processing issues
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>• Processing errors</div>
                  <div>• Low confidence scores</div>
                  <div>• Anomaly detection</div>
                </div>
                <Button className="w-full mt-4" variant="outline">
                  Use Template
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-purple-500" />
                  <span>Daily Reports</span>
                </CardTitle>
                <CardDescription>
                  Generate and send daily summaries
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>• Processing statistics</div>
                  <div>• Quality metrics</div>
                  <div>• Error summaries</div>
                </div>
                <Button className="w-full mt-4" variant="outline">
                  Use Template
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="agents">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>AI Agent Integration</CardTitle>
                <CardDescription>
                  Connect AI agents to automation workflows for intelligent processing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium">Select AI Agent</label>
                    <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose an AI agent..." />
                      </SelectTrigger>
                      <SelectContent>
                        {agents?.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <div className="flex items-center space-x-2">
                              <Bot className="h-4 w-4" />
                              <span>{agent.name}</span>
                              <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>
                                {agent.status}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      disabled={!selectedAgent || isExecuting}
                      onClick={() => {
                        if (selectedAgent) {
                          executeAgent({
                            agentId: selectedAgent,
                            inputData: {
                              trigger: 'manual_automation',
                              workflow_context: 'automation_integration'
                            }
                          });
                        }
                      }}
                    >
                      {isExecuting && <Clock className="h-4 w-4 mr-2 animate-spin" />}
                      Execute Agent
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {agents?.map((agent) => (
                    <Card key={agent.id} className="border-dashed">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center space-x-2">
                          <Bot className="h-4 w-4" />
                          <span>{agent.name}</span>
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {agent.description || `${agent.agent_type} agent`}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Type:</span>
                            <Badge variant="outline">{agent.agent_type}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Status:</span>
                            <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>
                              {agent.status}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Executions:</span>
                            <span>{agent.execution_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Success Rate:</span>
                            <span>{agent.success_rate}%</span>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-3"
                          onClick={() => setSelectedAgent(agent.id)}
                        >
                          Select Agent
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="scheduling">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Tasks</CardTitle>
                <CardDescription>
                  Manage automated tasks and scheduling configurations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <span>Daily Reports</span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Generate daily processing summaries
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Schedule:</span>
                          <span className="font-mono">18:00 daily</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <Badge variant="default">Active</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Last Run:</span>
                          <span>2 hours ago</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Next Run:</span>
                          <span>in 22 hours</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-3">
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <Bot className="h-4 w-4 text-green-500" />
                        <span>AI Agent Sync</span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Synchronize agent performance metrics
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Schedule:</span>
                          <span className="font-mono">*/30 * * * *</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <Badge variant="default">Active</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Last Run:</span>
                          <span>15 mins ago</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Next Run:</span>
                          <span>in 15 mins</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-3">
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-purple-500" />
                        <span>Performance Check</span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Monitor system performance metrics
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Schedule:</span>
                          <span className="font-mono">0 */4 * * *</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <Badge variant="default">Active</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Last Run:</span>
                          <span>1 hour ago</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Next Run:</span>
                          <span>in 3 hours</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-3">
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <Separator className="my-6" />

                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-lg font-semibold">Create New Scheduled Task</h4>
                    <p className="text-sm text-muted-foreground">
                      Add new automation tasks with custom schedules
                    </p>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Rule Modal */}
      {isCreatingRule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Create Automation Rule</CardTitle>
              <CardDescription>
                Define conditions and actions for automated workflows
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Rule Name</label>
                  <Input
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    placeholder="Enter rule name..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Trigger</label>
                  <Select 
                    value={newRule.trigger} 
                    onValueChange={(value) => setNewRule({...newRule, trigger: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bill_processed">Bill Processed</SelectItem>
                      <SelectItem value="schedule">Scheduled</SelectItem>
                      <SelectItem value="anomaly_detected">Anomaly Detected</SelectItem>
                      <SelectItem value="manual">Manual Trigger</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={newRule.description}
                  onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                  placeholder="Describe what this rule does..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={newRule.enabled}
                  onCheckedChange={(checked) => setNewRule({...newRule, enabled: checked})}
                />
                <label className="text-sm font-medium">Enable rule immediately</label>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreatingRule(false)}>
                  Cancel
                </Button>
                <Button onClick={createRule}>
                  Create Rule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};